import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { isNullOrUndefined } from '../../models/utils';
import { Endpoint, HelpIndex, MessageType, TagOperationType, UpdateStatusType, SaveToRemoveFromOperationType, ModalTargetType, OperationType, TemplateOperationType, EntityType, SettingsType } from '../../models/enums';
import { NavigationContentComponent } from '../../models/navigation-content-component';
import { Tag, TagApplication } from '../../models/tag';
import { TaggedEntity } from '../../models/tagged-entity';
import { Workflow, WorkflowStep } from '../../models/workflow';
import { CommonService } from '../../services/common/common.service';
import { HttpService } from '../../services/http/http.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { WorkflowMenu } from '../main-content/menus/workflow-menu';
import { CreateEditEntityModalComponent } from '../modals/create-edit-entity-modal/create-edit-entity-modal.component';
import { DeleteEntityModalComponent } from '../modals/delete-entity-modal/delete-entity-modal.component';
import { TagsModalComponent, TagsModalData } from '../modals/tags-modal/tags-modal.component';
import { TargetPickerModalComponent } from '../modals/target-picker-modal/target-picker-modal.component';
import { WorkflowReportingOptionsModalComponent } from '../modals/workflow-reporting-options-modal/workflow-reporting-options-modal.component';
import { TargetPickerTreeType } from '../target-picker-tree/target-picker-tree.component';
import { DynamicFolder } from '../../models/dynamic-folder';
import { ObjectActionListType, ActionType, Permissions, PermissionSet } from '../../models/permission';
import { AddToRemoveFromModalComponent } from '../modals/add-to-remove-from-modal/add-to-remove-from-modal.component';
import { SaveAsLoadFromTemplateComponent } from '../modals/settings-modal/save-as-load-from-template/save-as-load-from-template.component';
import { XMLService } from '../../services/xml/xml.service';
import { MetadataService, MetadataToken } from '../../services/metadata/metadata.service';

@Component({
    standalone: false,
    selector: 'res-workflow',
    templateUrl: './workflow.component.html',
    styleUrls: ['./workflow.component.scss']
})
export class WorkflowComponent implements NavigationContentComponent, OnInit, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    showValidationPopover = false;
    showMetadataPopover = false;
    metadataPopoverText: string = 'Field name error/s found. See the step/s marked below for details.';
    popoverType: string;
    public contentForm: UntypedFormGroup;
    workflow = new Workflow();
    searchTasks: Array<any> = [];
    isBusy = false;
    busyMessage = '';
    isValid: boolean;
    automationId = 'workflow';
    validityResults: Array<string> = [];
    validityText: string;
    private entityChangedEvent: Subscription;
    selectedTags: Array<TaggedEntity> = [];
    selectedFolder: DynamicFolder = null;
    tagsHandle = [];
    queriesChanged: boolean;
    metadataList: Array<MetadataToken> = [];

    private percentComplete: number;

    // permissions
    hasCreateTagPermissions: boolean;
    hasCreatePermissions: boolean;
    hasLockPermissions: boolean;
    hasDeletePermissions: boolean;
    hasCreateFolderPermissions: boolean;
    hasDocumentTagPermission: boolean;
    hasDocumentFolderPermission: boolean;

    getCompletion(): number {
        return this.percentComplete;
    }

    isPageDirty(): boolean {
        return this.contentForm ? this.contentForm.dirty : false;
    }

    identify(index: number, item: TaggedEntity): string {
        return item.handle;
    }

    constructor(private formBuilder: UntypedFormBuilder, private commonService: CommonService,
        private restService: RestService, private httpService: HttpService, private xmlService: XMLService, private metadataService: MetadataService) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if ((data === UpdateStatusType.WORK_FLOW) && (this.contentForm && !this.contentForm.dirty)) {
                setTimeout(() => {
                    this.getWorkflow();
                }, 250);
            }
        });
    }

    ngOnInit(): void {
        this.initForm();

        this.hasCreateTagPermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.PRJ_TAGS, permissionActions: [ActionType.CREATE] }]);
        this.hasCreatePermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.CREATE] }]);
        this.hasLockPermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.LOCK_UNLOCK_WORKFLOW, permissionActions: [ActionType.CREATE] }]);
        this.hasDeletePermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.DELETE] }]);
        this.hasDocumentTagPermission = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.DOCUMENT_TAGS, permissionActions: [ActionType.CREATE] }]);
        this.hasDocumentFolderPermission = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.DOCUMENT_FOLDER, permissionActions: [ActionType.CREATE] }]);
        this.metadataService.tokenizationTypes().then(responseTokenizations => {
            this.metadataList = responseTokenizations;
        });
    }

    initForm(): void {
        if (!this.contentForm) {
            this.contentForm = this.formBuilder.group({
                queryItems: this.formBuilder.array([]),
                dataSetItem: new UntypedFormGroup({
                    attributes: new UntypedFormControl(),
                    description: new UntypedFormControl(),
                    handle: new UntypedFormControl(),
                    name: new UntypedFormControl(),
                    type: new UntypedFormControl()
                })
            });
        }
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
        this.workflow = undefined;
    }

    selectedTreeItemChanged = (node: GenericTreeNode): void => {
        this.validityResults = [];
        this.isValid = !node?.data?.queriesChanged && node.data?.queryItems?.items?.length > 0;
        this.workflow = new Workflow();
        this.searchTasks = [];

        this.showValidationPopover = false;
        this.showMetadataPopover = false;
        this.initForm();
        this.getWorkflow(node.data.handle);
    }

    getWorkflow(handle?: string): void {
        if (!handle && (!this.workflow || !this.workflow.handle)) {
            return;
        }
        this.busyMessage = 'Loading Workflow ...';
        this.isBusy = true;
        this.restService.post(Endpoint.workFlowManager_getWorkFlow, { handle: handle ? handle : this.workflow.handle }).then(response => {
            if (response && response.workFlow) {
                if (isNullOrUndefined(response.workFlow.dataSetItem)) {
                    response.workFlow.dataSetItem = { name: '' };
                }
                this.queriesChanged = response.workFlow.queriesChanged;

                this.workflow = new Workflow(response.workFlow);
                if (handle && this.workflow.hasRunError) {
                    this.commonService.openErrorModal('Unexpected run error. Please rerun the workflow.');
                }
                const resultCount = this.workflow.attributes.filter(attr => attr.name === 'resultcount');
                if (resultCount.length > 0) {
                    this.workflow.resultCount = Number.parseInt(resultCount[0].value, 10);
                }
                if (this.contentForm) {
                    this.contentForm.patchValue({ dataSetItem: this.workflow.dataSetItem });
                }
                this.searchTasks = [];
                let taskHandle = this.workflow ? this.workflow.taskHandle : null;
                if (!taskHandle && this.workflow.attributes.filter(attr => attr.name === 'lastExecutionHandle').length > 0) {
                    taskHandle = this.workflow.attributes.filter(attr => attr.name === 'lastExecutionHandle')[0].value;
                }
                if (taskHandle) {
                    this.getTask(taskHandle);
                }
                if (response.workFlow.tagItem) {
                    this.restService.post(Endpoint.workingSetManager_listWorkingSetViews, {
                        workingSetHandle: this.commonService.projectData.handle,
                        count: 0,
                        startIndex: 0,
                        filters: [{
                            attribute: 'viewType',
                            value: 'TAG',
                            operator: 'EQUALS'
                        }]
                    }).then(responseTags => {
                        if (responseTags && responseTags.views) {
                            this.isBusy = true;
                            const tagList = (<Array<any>>responseTags.views).map(t => new Tag(t));;
                            response.workFlow.tagItem.taggedEntityList = [];
                            response.workFlow.tagItem.tags.forEach((entry) => {
                                const tag = _.filter(tagList, w => w.handle === entry);
                                if (tag && tag.length > 0) {
                                    response.workFlow.tagItem.taggedEntityList.push(tag[0]);
                                }
                            });
                            this.selectedTags = response.workFlow.tagItem.taggedEntityList;
                            this.isBusy = false;
                        }
                    });
                } else {
                    this.selectedTags = null;
                }
                if (response.workFlow.folderItem) {
                    this.selectedFolder = this.workflow.folderItem ? new DynamicFolder(this.workflow.folderItem) : null;
                    this.selectedFolder.iconColor = (this.workflow.folderItem.attributes && this.workflow.folderItem.attributes.length > 0) ?
                        this.workflow.folderItem.attributes.filter(attr => attr.name === 'folderColor')[0].value : '';
                } else {
                    this.selectedFolder = null;
                }
                this.setQueryItems(this.workflow.queryItems.items);
                this.validityResults = _.map(response.workFlow.queryItems.items, (validation) => validation.errorMessage);
                // TODO - This is a workaround until a flag from backend will be implemented
                this.workflow.queryItems.items.forEach((step: any) => {
                    step.isWarning = !!step.errorMessage?.startsWith('Warning:');
                });
                const invalidSteps = this.validityResults.filter(item => item);
                if (invalidSteps.length > 0) {
                    this.displayValidationError(invalidSteps.length);
                }
                this.isValid = Boolean(this.workflow.queryItems.items.length) && invalidSteps.length === 0;
                if (this.contentForm) {
                    this.contentForm.markAsPristine();
                }
                this.isBusy = false;
            }
        }).catch(error => {
            this.isBusy = false;
        });
    }

    getTask(handle: string): void {
        this.busyMessage = 'Loading Workflow steps ...';
        this.isBusy = true;
        this.restService.post(Endpoint.taskManager_getTasks, {
            includeDrDebug: false,
            includeResourceStatistics: false,
            taskHandles: [handle]
        }).then(response => {
            const taskCandidate = response && response.tasks ? response.tasks[0] : null;
            const lastSearchHandle = taskCandidate && taskCandidate.attributes ?
                taskCandidate.attributes.filter(attr => attr.name === 'lastsearchhandle') : null;
            if (taskCandidate) {
                if (taskCandidate.handle === this.workflow.taskHandle) {
                    this.percentComplete = taskCandidate.percentComplete;
                    if (taskCandidate.percentComplete < 100) {
                        this.httpService.pollForTask(taskCandidate.handle, this.runningTaskCallback.bind(this));
                    }
                }
                this.searchTasks.push(taskCandidate);
            }
            if (lastSearchHandle && lastSearchHandle.length > 0) {
                this.getTask(lastSearchHandle[0].value);
            } else {
                this.isBusy = false;
            }
        }).catch(error => {
            this.isBusy = false;
        });
    }

    get queryItems(): UntypedFormArray {
        return this.contentForm.get('queryItems') as UntypedFormArray;
    }

    setQueryItems(steps: any[]): void {
        while (this.queryItems.length > 0) {
            this.queryItems.removeAt(0);
        }
        if (steps.length === 0) {
            this.queryItems.push((new WorkflowStep()).getForm());
        } else {
            steps.forEach(element => {
                const elementCopy = _.cloneDeep(element);
                const newStep = new WorkflowStep(elementCopy);
                this.queryItems.push(newStep.getForm());
            });
        }
    }

    launchPicker(): void {
        if (!this.workflow.lockedBy && !this.isRunning()) {
            this.commonService.openModal(TargetPickerModalComponent, {
                modalData: {
                    targetPickerTreeType: TargetPickerTreeType.selectWorkflowTarget,
                    initialSelectedNodeData: this.contentForm.value.dataSetItem
                },
                class: 'horizontal-px-700 vertical-percent-80'
            }).then(response => {
                if (response && response.modalResult && response.modalData.target) {
                    response.modalData.target.data.name = response.modalData.target.data.name.replace('CaseData', 'Project Data');
                    this.contentForm.patchValue({ dataSetItem: response.modalData.target.data });
                    this.contentForm.markAsDirty();
                }
            });
        }
    }

    discardChanges(): void {
        this.showMetadataPopover = false;
        this.contentForm.reset(this.workflow);
        this.setQueryItems(this.workflow.queryItems.items);
        this.contentForm.markAsPristine();
        this.getWorkflow();
    }

    isLockable(): boolean {
        return !this.contentForm.dirty && !this.isRunning();
    }

    isRunning(): boolean {
        if (this.workflow && this.workflow.attributes) {
            const attr = _.find(this.workflow.attributes, (attribute) => attribute.name === 'isRunning');
            if (attr) {
                return attr.value === 'true';
            }
        }
        return false;
    }

    validate(): void {
        this.isValid = false;
        this.showValidationPopover = false;
        this.busyMessage = 'Validating Workflow ...';
        this.isBusy = true;
        this.restService.post(Endpoint.workFlowManager_validateWorkFlow,
            { workFlowHandle: this.workflow.handle, taskDescription: 'Validating Workflow ' + this.workflow.name }).then((response) => {
                this.isBusy = false;
                this.restService.post(Endpoint.workFlowManager_validateWorkFlowResult,
                    { taskHandle: response.handle }).then((result) => {
                        this.getWorkflow();
                        this.isBusy = false;
                        if (!(result?.numberOfErrors > 0)) {
                            this.isValid = true;
                            this.validityText = 'All queries are valid.';
                            this.popoverType = 'success';
                            this.showValidationPopover = true;
                        }
                    }).catch(() => { this.isBusy = false; });
            }).catch(() => { this.isBusy = false; });
    }

    private displayValidationError(quantity: number): void {
        if (quantity > 1) {
            this.validityText = 'Query errors found. See the steps marked below for details.';
        } else {
            this.validityText = 'Query error found. See the step marked below for details.';
        }
        this.popoverType = 'error';
        this.showValidationPopover = true;
    }

    run(): void {
        this.showValidationPopover = false;
        this.commonService.openModal(WorkflowReportingOptionsModalComponent, {
            modalData: this.workflow,
            class: 'horizontal-px-700'
        }).then((response) => {
            if (response && response.modalResult) {
                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.WORK_FLOW);
                this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.RESULT_VIEW);
                this.percentComplete = 0;
                this.getWorkflow();
            }
        });
    }

    runningTaskCallback(response: any): void {
        this.percentComplete = response.percentComplete;
        if (this.percentComplete === 100 && this.contentForm && !this.contentForm.dirty) {
            this.getWorkflow();
        }
    }

    navigateToResults(): void {
        this.commonService.sendMessage(MessageType.navigationRequest, this.searchTasks[this.searchTasks.length - 1]);
    }

    saveChangesWithPromise(): Promise<boolean> {
        this.showValidationPopover = false;
        this.showMetadataPopover = false;
        this.busyMessage = 'Saving Workflow ...';
        this.isBusy = true;
        let steps = [];
        this.setTagsHandle();

        const hasInvalidMetadata = this.validateQueryMetadataFieldsInWorkflow();
        if (hasInvalidMetadata) {
            this.isBusy = false;
            this.isValid = false;
            this.showMetadataPopover = true;
            return Promise.resolve(false);
        }

        if (this.queryItems.length >= 1 || this.queryItems.dirty) {
            steps = _.map(_.filter(this.queryItems.value, value => value.query && value.query.length > 0), (step, index) => {
                return {
                    attributes: [{ name: 'steptype', value: step.isDateQuery ? 'Date Range Query' : 'Term Query' }],
                    comment: step.comment,
                    description: step.description,
                    expandFields: step.expandFields,
                    includeFamilyMembers: step.includeFamilyMembers,
                    includeSynonyms: step.includeSynonyms,
                    reportRequests: step.reportRequests,
                    orderOfExecution: index + 1,
                    query: step.query,
                    type: 'QUERY',
                    errorMessage: step.errorMessage
                };
            });
        }

        return ((steps.length < this.queryItems.length) ? this.commonService.openConfirmationModal('One or more steps are missing a query.', 'Confirmation',
            'Discard the blank step/s and Save', 'Cancel').then(response => {
                return response.modalResult;
            }) : Promise.resolve(true)).then(save => {
                if (save) {
                    return this.restService.post(Endpoint.workFlowManager_updateWorkFlow, {
                        name: this.workflow.name,
                        handle: this.workflow.handle,
                        description: this.workflow.description,
                        workFlow: {
                            createdBy: this.workflow.createdBy,
                            createdOn: this.workflow.createdOn,
                            dataSetItem: {
                                attributes: this.contentForm.value.dataSetItem.attributes,
                                description: this.contentForm.value.dataSetItem.description,
                                handle: this.contentForm.value.dataSetItem.handle,
                                name: this.contentForm.value.dataSetItem.name,
                                type: 'DATASET'
                            },
                            description: this.workflow.description,
                            handle: this.workflow.handle,
                            // lockedBy ...
                            modifiedBy: this.workflow.modifiedBy,
                            modifiedOn: this.workflow.modifiedOn,
                            name: this.workflow.name,
                            projectHandle: this.commonService.currentProject.handle,
                            queryItems: {
                                items: steps
                            },
                            sourceViewHandle: null,
                            archived: this.workflow.archived,
                            version: this.workflow.version,
                            tagItem: this.selectedTags ? {
                                type: 'TAG',
                                tags: this.tagsHandle,
                                orderOfExecution: steps && steps.length > 0 ? steps.length + 1 : 1,
                            } : null,
                            folderItem: this.selectedFolder !== null ? {
                                attributes: [{ name: 'folderColor', value: this.selectedFolder.iconColor }],
                                type: 'FOLDER',
                                handle: this.selectedFolder.handle,
                                name: this.selectedFolder.name,
                                description: this.selectedFolder.description,
                                orderOfExecution: this.selectedTags && this.selectedTags.length > 0 ? steps.length + 2 : steps.length + 1,
                            } : null
                        }
                    }).then(modalResponse => {
                        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.WORK_FLOW);
                        this.workflow = new Workflow(modalResponse.workFlow);
                        this.contentForm.markAsPristine();
                        this.isBusy = false;
                        this.getWorkflow();
                        return Promise.resolve(true);
                    }).catch(error => {
                        this.isBusy = false;
                        return Promise.resolve(false);
                    });
                } else {
                    this.isBusy = false;
                    return Promise.resolve(false);
                }
            });
    }

    saveToTemplate(): void {
        this.commonService.openModal(SaveAsLoadFromTemplateComponent, {
            class: 'vertical-percent-80',
            modalData: {
                operationType: TemplateOperationType.saveAs, selectedItem: this.workflow,
                templateType: EntityType.workFlow, permissions: new PermissionSet({ permissionType: ObjectActionListType.WORK_FLOW, permissionActions: [ActionType.VIEW] }),
                isSystemContext: false,
                currentOrg: this.commonService.currentUser?.customerName
            }
        });
    }

    copy(): void {
        const nameless = new Workflow({});
        Object.assign(nameless, this.workflow);
        nameless.name = '';
        this.commonService.openModal(CreateEditEntityModalComponent,
            { modalData: nameless, options: { copy: true } }).then(response => {
                if (response.modalResult && response.modalData && response.modalData.workFlow) {
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.WORK_FLOW);
                    this.commonService.sendMessage(MessageType.navigationRequest, response.modalData.workFlow);
                }
            });
    }

    lock(action: string): void {
        if (this.isLockable()) {
            this.busyMessage = action + 'ing ...';
            this.isBusy = true;
            this.restService.post(action === 'Lock' ? Endpoint.workFlowManager_lockWorkFlow : Endpoint.workFlowManager_unlockWorkFlow,
                { handle: this.workflow.handle }).then(response => {
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.WORK_FLOW);
                    this.isBusy = false;
                }).catch(reason => {
                    this.isBusy = false;
                });
        }
    }

    edit(): void {
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: this.workflow }).then(() => {
            this.getWorkflow();
        });
    }

    delete(): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: this.workflow as Workflow, options: { suppressMessage: true } }).then(delResponse => {
            this.commonService.sendMessage(MessageType.navigationRequest, WorkflowMenu);
        });
    }

    onStepQueryChanged(stepIndex: number): void {
        this.showMetadataPopover = false;
        if (this.validityResults) {
            this.validityResults[stepIndex] = null;
        }
    }

    hasNonEmptyStep(): boolean {
        return _.some(this.queryItems.value, stepValue => !isNullOrUndefined(stepValue.query));
    }

    hasEmptyStep(): boolean {
        return _.some(this.queryItems.value, stepValue => !stepValue.query);
    }

    addStep(index: number): void {
        this.queryItems.insert(index, (new WorkflowStep()).getForm());
        this.queryItems.markAsDirty();
        if (this.validityResults) {
            const before = index > 0 ? this.validityResults.slice(0, index) : [];
            const after = this.validityResults.length > index ? this.validityResults.slice(index) : [];
            this.validityResults = before.concat([null], after);
        }
    }

    removeStep(index: number): void {
        this.queryItems.removeAt(index);
        this.queryItems.markAsDirty();
        if (this.validityResults) {
            this.validityResults = this.validityResults.filter((value, listIndex) => {
                return listIndex !== index;
            });
            if (this.validityResults.length === 0) {
                this.showValidationPopover = false;
            }
        }
        if (this.queryItems.length === 0) {
            this.queryItems.push((new WorkflowStep()).getForm());
        }
    }

    moveUp(step: number): void {
        const control = this.queryItems.at(step);
        this.queryItems.removeAt(step);
        this.queryItems.insert(step - 1, control);
        this.queryItems.markAsDirty();
        if (this.validityResults) {
            const tempValidityResult = this.validityResults[step];
            this.validityResults[step] = this.validityResults[step - 1];
            this.validityResults[step - 1] = tempValidityResult;
        }
    }

    openHelp = (): void => {
        this.commonService.openHelp(HelpIndex.myedisc_edit_run_open_workflow_dlg);
    }

    selectTag(): void {
        if (!this.workflow.lockedBy && !this.isRunning()) {
            this.commonService.openModal(TagsModalComponent, {
                modalData: <TagsModalData>{
                    operationType: TagOperationType.selectTagStateList,
                    selectedTags: this.selectedTags !== null ? this.selectedTags.map(t => new TagApplication({
                        tagHandle: t.handle
                    })) : null
                }
            }).then(response => {
                if (response && response.modalResult && response.modalData) {
                    this.selectedTags = (response.modalData as Array<TaggedEntity>);
                    this.contentForm.markAsDirty();
                    this.setTagsHandle();
                }
            });
        }
    }

    setTagsHandle(): void {
        if (this.selectedTags) {
            this.tagsHandle = [];
            this.selectedTags.forEach(element => {
                this.tagsHandle.push(element.handle);
            });
        }
    }

    clearTag(index: number): void {
        if (!this.workflow.lockedBy && !this.isRunning()) {
            this.selectedTags.splice(index, 1);
            this.selectedTags = this.selectedTags.length !== 0 ? this.selectedTags : null;
            this.tagsHandle = null;
            this.tagsHandle = this.selectedTags ? this.selectedTags.map(t => t.handle) : null;
            this.contentForm.markAsDirty();
        }
    }

    clearFolder(): void {
        if (!this.workflow.lockedBy && !this.isRunning()) {
            this.selectedFolder = null;
            this.contentForm.markAsDirty();
        }
    }

    selectFolder(): void {
        if (!this.workflow.lockedBy && !this.isRunning()) {
            this.commonService.openModal(AddToRemoveFromModalComponent, {
                modalData: {
                    operationType: SaveToRemoveFromOperationType.add,
                    targetType: ModalTargetType.selection
                }
            }).then(response => {
                if (response && response.modalResult && response.modalData) {
                    const selectedFolder = (response.modalData.data as DynamicFolder);
                    this.selectedFolder = selectedFolder;
                    this.contentForm.markAsDirty();
                }
            });
        }
    }

    validateQueryMetadataFieldsInWorkflow(): boolean {
        const queries = this.queryItems?.value;
        let hasInvalidMetadata = false;

        for (let i = 0; i < queries.length; i++) {
            const query = this.xmlService.xmlToQueryConverter(queries[i].query) as string;
            const invalidMetadataResults = this.metadataService.findInvalidMetadataFields(query, this.metadataList, SettingsType.project);

            if (invalidMetadataResults.length > 0) {
                const firstInvalid = invalidMetadataResults[0];
                queries[i].errorMessage = `Invalid metadata field \"${firstInvalid.fullMatch}\"`;
                hasInvalidMetadata = true;
            }
        }

        this.validityResults = _.map(queries, (validation) => validation.errorMessage);
        return hasInvalidMetadata;
    }
}
